import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Menu, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const MENU_LINKS = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/properties', label: 'Properties' },
  { to: '/renewals', label: 'Renewals' },
  { to: '/todo', label: 'To Do' },
];

export default function Navbar({ back }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close on outside click + Esc
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logged out');
    navigate('/');
  };

  const handleBack = () => {
    if (typeof back === 'string') navigate(back);
    else if (back && back.to) navigate(back.to);
    else if (back && typeof back.onClick === 'function') back.onClick();
  };

  const backLabel = (back && back.label) || 'Back';

  return (
    <header className="app-navbar" data-testid="app-navbar">
      <div className="app-navbar-inner">
        <div className="navbar-slot navbar-slot-left">
          {back && (
            <button
              type="button"
              onClick={handleBack}
              className="navbar-back"
              data-testid="navbar-back"
            >
              <ArrowLeft size={14} />
              <span>{backLabel}</span>
            </button>
          )}
        </div>

        <button
          type="button"
          className="navbar-logo"
          onClick={() => navigate('/dashboard')}
          data-testid="navbar-logo"
          aria-label="Rental P.A. — back to dashboard"
        >
          <img src="/logo-transparent.png" alt="Rental P.A." className="navbar-logo-img" />
        </button>

        <div className="navbar-slot navbar-slot-right">
          <button
            ref={triggerRef}
            type="button"
            className="navbar-menu-trigger"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            data-testid="navbar-menu-trigger"
          >
            <Menu size={22} />
          </button>

          {menuOpen && (
            <div
              ref={menuRef}
              className="navbar-dropdown"
              role="menu"
              data-testid="navbar-dropdown"
            >
              {MENU_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `navbar-dropdown-item${isActive ? ' navbar-dropdown-item-active' : ''}`
                  }
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                  data-testid={`navbar-menu-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={handleLogout}
                className="navbar-dropdown-item navbar-dropdown-logout"
                role="menuitem"
                data-testid="navbar-menu-logout"
              >
                <LogOut size={14} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
